import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { _T, C, CSS } from '../shared/theme';
import { useStorage } from '../shared/hooks';
import { syncReminderPrefs, subscribeToPush, unsubscribeFromPush } from '../shared/dbService';

const OFFSET_OPTIONS = [
  { v: 0,  label: 'Op tijd',  labelEN: 'At time' },
  { v: 5,  label: '5 min',    labelEN: '5 min' },
  { v: 10, label: '10 min',   labelEN: '10 min' },
  { v: 15, label: '15 min',   labelEN: '15 min' },
  { v: 30, label: '30 min',   labelEN: '30 min' },
  { v: 60, label: '1 uur',    labelEN: '1 hour' },
];

const TZ_OPTIONS = [
  { v: -60,  label: 'UTC-1' },
  { v: 0,    label: 'UTC+0' },
  { v: 60,   label: 'UTC+1' },
  { v: 120,  label: 'UTC+2' },
  { v: 180,  label: 'UTC+3' },
  { v: 240,  label: 'UTC+4' },
  { v: 300,  label: 'UTC+5' },
  { v: 360,  label: 'UTC+6' },
];

export default function RemindersSheet({ lang, onClose }) {
  const nl = lang === 'nl';
  const [reminderOffset, setReminderOffset] = useStorage('sl_reminder_offset_mins', 15);
  const [tzOffset,       setTzOffset]       = useStorage('sl_tz_offset_mins', 60);
  const [notifPerm,    setNotifPerm]    = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [pushEnabled,  setPushEnabled]  = useState(() =>
    localStorage.getItem('sl_push_enabled') !== 'false'
  );

  const save = (off, tz) => syncReminderPrefs(off, tz);

  const enablePush = async () => {
    if (!('Notification' in window)) return;
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === 'granted') {
      localStorage.setItem('sl_push_enabled', 'true');
      setPushEnabled(true);
      await subscribeToPush();
    }
  };

  const togglePush = async () => {
    if (notifPerm === 'denied') return;
    if (notifPerm !== 'granted') { await enablePush(); return; }
    if (pushEnabled) {
      localStorage.setItem('sl_push_enabled', 'false');
      setPushEnabled(false);
      await unsubscribeFromPush();
    } else {
      localStorage.setItem('sl_push_enabled', 'true');
      setPushEnabled(true);
      await subscribeToPush();
    }
  };

  const Row = ({ label, right, border = true }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: border ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
      <span style={{ color: C.white, fontSize: 13 }}>{label}</span>
      {right}
    </div>
  );

  const ChipRow = ({ options, value, onChange }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 12 }}>
      {options.map(o => (
        <button key={o.v} onClick={() => onChange(o.v)}
          style={{ background: value === o.v ? `${_T.accent}22` : _T.bg4, border: `1px solid ${value === o.v ? _T.accent : _T.cardBorderBot}`, color: value === o.v ? _T.accent : C.silver, borderRadius: 20, padding: '5px 13px', cursor: 'pointer', fontSize: 12, fontWeight: value === o.v ? 700 : 400 }}>
          {nl ? o.label : (o.labelEN || o.label)}
        </button>
      ))}
    </div>
  );

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,8,18,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 480, background: _T.bg2, borderRadius: '24px 24px 0 0', border: `1px solid ${_T.cardBorderTop}`, padding: '24px 24px 36px', maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} style={{position:'absolute',top:14,right:14,background:'transparent',border:'none',color:C.silver,fontSize:20,cursor:'pointer',padding:4,lineHeight:1,zIndex:2}}>✕</button>
        <style>{CSS}</style>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: _T.cardBorderBot, margin: '0 auto 20px' }} />

        <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 4 }}>INSTELLINGEN</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: C.white, marginBottom: 20 }}>
          {nl ? 'Herinneringen 🔔' : 'Reminders 🔔'}
        </div>

        {/* Push permission */}
        <div style={{ background: _T.bg3, borderRadius: 14, padding: '16px', marginBottom: 16, border: `1px solid ${_T.cardBorderTop}` }}>
          <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 12 }}>
            {nl ? 'PUSH MELDINGEN' : 'PUSH NOTIFICATIONS'}
          </div>
          <Row border={false}
            label={<div>
              <div style={{ color: C.white, fontSize: 13 }}>{nl ? 'Push meldingen' : 'Push notifications'}</div>
              <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
                {notifPerm === 'denied'
                  ? (nl ? 'Geblokkeerd — sta toe in browser' : 'Blocked — allow in browser settings')
                  : notifPerm !== 'granted'
                  ? (nl ? 'Nog niet ingeschakeld' : 'Not yet enabled')
                  : pushEnabled ? (nl ? '✓ Ingeschakeld' : '✓ Enabled') : (nl ? 'Uitgeschakeld' : 'Disabled')}
              </div>
            </div>}
            right={notifPerm === 'denied'
              ? <span style={{ color: C.red, fontSize: 11 }}>{nl ? 'Geblokkeerd' : 'Blocked'}</span>
              : notifPerm !== 'granted'
              ? <button onClick={enablePush} style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}`, color: _T.accent, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  {nl ? 'Inschakelen' : 'Enable'}
                </button>
              : <button onClick={togglePush} style={{ position: 'relative', width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: pushEnabled ? C.teal : _T.bg4, transition: 'background 0.2s', padding: 0 }}>
                  <span style={{ position: 'absolute', top: 3, left: pushEnabled ? 24 : 3, width: 20, height: 20, borderRadius: '50%', background: C.white, transition: 'left 0.2s', display: 'block' }} />
                </button>}
          />
        </div>

        {/* Reminder offset */}
        <div style={{ background: _T.bg3, borderRadius: 14, padding: '16px', marginBottom: 16, border: `1px solid ${_T.cardBorderTop}` }}>
          <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 4 }}>
            {nl ? 'HERINNERING VOOR AANVANG' : 'REMIND BEFORE START'}
          </div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 12 }}>
            {nl ? 'Hoe ver van tevoren wil je een melding?' : 'How early do you want to be notified?'}
          </div>
          <ChipRow
            options={OFFSET_OPTIONS}
            value={reminderOffset}
            onChange={v => { setReminderOffset(v); save(v, tzOffset); }}
          />
        </div>

        {/* Timezone */}
        <div style={{ background: _T.bg3, borderRadius: 14, padding: '16px', border: `1px solid ${_T.cardBorderTop}` }}>
          <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 4 }}>
            {nl ? 'TIJDZONE' : 'TIMEZONE'}
          </div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 12 }}>
            {nl ? 'Selecteer jouw UTC-offset voor correcte timing.' : 'Select your UTC offset for correct timing.'}
          </div>
          <ChipRow
            options={TZ_OPTIONS}
            value={tzOffset}
            onChange={v => { setTzOffset(v); save(reminderOffset, v); }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
